import { AsyncPipe } from '@angular/common'
import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, NavigationSkipped, NavigationStart, Router } from '@angular/router'
import { User } from '@ftr/contracts/api/user'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  BreadcrumbService,
  ButtonComponent,
  IconComponent,
  isMobileScreenSize,
  ScreenSize,
  ScreenSizeService,
  TooltipModule,
  unwrapData,
} from '@ftr/foundation'
import { APP_MODE } from '@ftr/routing-paths'
import { UserAvatarComponent, UserState } from '@ftr/ui-user'
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngxs/store'
import { Options } from '@popperjs/core'
import { Observable } from 'rxjs'
import { PersonalMenuComponent } from '../personal-menu/personal-menu.component'

@Component({
  selector: 'ftr-account-dropdown',
  templateUrl: './account-dropdown.component.html',
  styleUrls: ['./account-dropdown.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgbPopoverModule,
    UserAvatarComponent,
    AsyncPipe,
    ButtonComponent,
    PersonalMenuComponent,
    TooltipModule,
    IconComponent,
  ],
})
export class AccountDropdownComponent implements OnInit {
  private readonly store = inject(Store)
  private readonly screenSizeService = inject(ScreenSizeService)
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly breadcrumbService = inject(BreadcrumbService)
  private readonly appMode = inject(APP_MODE)
  readonly #destroyRef = inject(DestroyRef)

  currentCourtSystemId = input<Uuid>()
  protected personalMenuPopover = viewChild.required<NgbPopover>('personalMenuPopOver')

  user$: Observable<User>
  showingBreadcrumbs = signal<boolean>(false)

  private focusTimeout: ReturnType<typeof setTimeout>
  protected readonly ftrLogoPath = '/assets/images/logo-themed-no-text.svg'

  protected readonly popperOptions = (options: Partial<Options>): Partial<Options> => {
    // Use default options for desktop
    if (!isMobileScreenSize(this.currentScreenSize())) {
      return options
    }
    // Delegate positioning to custom css class
    options.modifiers = [
      {
        name: 'applyStyles',
        enabled: false,
      },
    ]
    return options
  }

  private currentScreenSize = signal<ScreenSize | undefined>(undefined)

  constructor() {
    effect(() => {
      const size = this.screenSizeService.sizeSignal()

      untracked(() => {
        const shouldReopen = this.personalMenuPopover()?.isOpen() && this.currentScreenSize() !== size
        this.currentScreenSize.set(size)
        // Popper modifiers/styles aren't dynamic, that's why we close the popover when
        // resizing to and from isMobile for a smoother experience
        if (shouldReopen) {
          this.personalMenuPopover().close()
          this.personalMenuPopover().open()
        }
      })
    })
  }

  ngOnInit(): void {
    this.user$ = this.store.select(UserState.user).pipe(unwrapData())
    this.watchRouterEvents()
  }

  onFocusIn(): void {
    clearTimeout(this.focusTimeout)
  }

  // Hide popover when focus is lost
  onFocusOut(): void {
    this.focusTimeout = setTimeout(() => {
      this.personalMenuPopover()?.close()
    }, 0)
  }

  get personalMenuText(): string {
    const isOpen = this.personalMenuPopover()?.isOpen() ?? false
    return `${isOpen ? 'Opened' : 'Closed'} Personal Menu, Select to ${isOpen ? 'Close' : 'Open'}`
  }

  private watchRouterEvents(): void {
    // Close the personal menu if we try to navigate away from the current page
    this.router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(event => {
      if (
        (event instanceof NavigationStart || event instanceof NavigationSkipped) &&
        this.personalMenuPopover().isOpen()
      ) {
        this.personalMenuPopover().close()
      }
      if (event instanceof NavigationEnd) {
        this.showingBreadcrumbs.set(this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute, this.appMode()))
      }
    })
  }
}
